/**
 * The26 dashboard UI — Modal, Dropdown, Collapse, Tooltip
 * Native JS + Tailwind utility classes (no Bootstrap).
 */
(function (global) {
  const instances = new WeakMap();

  function el(ref) {
    if (!ref) return null;
    return typeof ref === "string" ? document.querySelector(ref) : ref;
  }

  function bool(val, fallback) {
    if (val === undefined || val === null || val === "") return fallback;
    if (val === "false" || val === false) return false;
    if (val === "true" || val === true) return true;
    return val;
  }

  function emit(node, name, detail) {
    return node.dispatchEvent(
      new CustomEvent(name, { bubbles: true, cancelable: true, detail })
    );
  }

  function targetSelector(trigger) {
    return (
      trigger.getAttribute("data-target") ||
      trigger.getAttribute("data-bs-target") ||
      ((trigger.getAttribute("href") || "").charAt(0) === "#"
        ? trigger.getAttribute("href")
        : null)
    );
  }

  // ─── Modal ─────────────────────────────────────────────────────────
  class Modal {
    constructor(element, config = {}) {
      this._element = el(element);
      if (!this._element) throw new Error("Modal element not found");
      const ds = this._element.dataset;
      this._config = {
        backdrop: bool(ds.backdrop ?? ds.bsBackdrop, true),
        keyboard: bool(ds.keyboard ?? ds.bsKeyboard, true),
        focus: true,
        ...config,
      };
      if ((ds.backdrop ?? ds.bsBackdrop) === "static") this._config.backdrop = "static";
      this._isShown = false;
      this._backdrop = null;
      this._busy = false;
      // Ensure base Tailwind positioning if missing after migration
      this._element.classList.add("fixed", "inset-0", "z-1055", "overflow-y-auto", "outline-none");
      if (!this._element.classList.contains("hidden") && !this._isShown) {
        this._element.classList.add("hidden");
      }
      instances.set(this._element, this);
    }

    static getInstance(element) {
      return instances.get(el(element)) || null;
    }

    static getOrCreateInstance(element, config) {
      return Modal.getInstance(element) || new Modal(element, config);
    }

    toggle(relatedTarget) {
      return this._isShown ? this.hide() : this.show(relatedTarget);
    }

    show(relatedTarget) {
      if (this._isShown || this._busy) return;
      if (!emit(this._element, "show.bs.modal", { relatedTarget })) return;

      document.querySelectorAll("[data-modal].is-open, .is-open[id].fixed").forEach((node) => {
        if (node !== this._element && Modal.getInstance(node)) {
          Modal.getInstance(node).hide();
        }
      });
      // Also close other open modals tracked by hidden class removal
      document.querySelectorAll(".fixed.inset-0.z-\\[1055\\]:not(.hidden)").forEach((node) => {
        if (node !== this._element) {
          try {
            Modal.getOrCreateInstance(node).hide();
          } catch (_) {}
        }
      });

      this._isShown = true;
      this._busy = true;
      document.documentElement.classList.add("modal-open");
      document.body.classList.add("modal-open");
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight =
        window.innerWidth - document.documentElement.clientWidth + "px";

      this._showBackdrop();
      this._element.classList.remove("hidden");
      this._element.classList.add("is-open", "block");
      this._element.removeAttribute("aria-hidden");
      this._element.setAttribute("aria-modal", "true");
      this._element.setAttribute("role", "dialog");
      this._element.scrollTop = 0;

      const done = () => {
        this._busy = false;
        if (this._config.focus) {
          const focusable = this._element.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          (focusable || this._element).focus?.();
        }
        emit(this._element, "shown.bs.modal", { relatedTarget });
      };
      setTimeout(done, 150);
    }

    hide() {
      if (!this._isShown || this._busy) return;
      if (!emit(this._element, "hide.bs.modal")) return;
      this._isShown = false;
      this._busy = true;
      this._element.classList.add("hidden");
      this._element.classList.remove("is-open", "block", "show");

      const done = () => {
        this._element.setAttribute("aria-hidden", "true");
        this._element.removeAttribute("aria-modal");
        this._element.removeAttribute("role");
        this._hideBackdrop();
        if (!document.querySelector(".is-open.fixed")) {
          document.documentElement.classList.remove("modal-open");
          document.body.classList.remove("modal-open");
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";
          document.body.style.paddingRight = "";
        }
        this._busy = false;
        emit(this._element, "hidden.bs.modal");
      };
      setTimeout(done, 150);
    }

    dispose() {
      instances.delete(this._element);
      this._element = null;
    }

    handleUpdate() {}

    _showBackdrop() {
      if (this._config.backdrop === false) return;
      this._backdrop = document.createElement("div");
      this._backdrop.className =
        "fixed inset-0 z-[1050] bg-black/45 backdrop-blur-[2px] opacity-0 transition-opacity duration-150";
      document.body.appendChild(this._backdrop);
      requestAnimationFrame(() => this._backdrop.classList.replace("opacity-0", "opacity-100") || this._backdrop.classList.add("opacity-100"));
      this._backdrop.classList.remove("opacity-0");
      this._backdrop.classList.add("opacity-100");
      this._backdrop.addEventListener("click", () => {
        if (this._config.backdrop === "static") {
          this._element.classList.add("scale-[1.01]");
          setTimeout(() => this._element.classList.remove("scale-[1.01]"), 150);
          return;
        }
        this.hide();
      });
    }

    _hideBackdrop() {
      if (!this._backdrop) return;
      const node = this._backdrop;
      this._backdrop = null;
      node.classList.remove("opacity-100");
      node.classList.add("opacity-0");
      setTimeout(() => node.remove(), 150);
    }
  }

  // ─── Dropdown ──────────────────────────────────────────────────────
  class Dropdown {
    constructor(element) {
      this._element = el(element);
      this._menu =
        this._element?.nextElementSibling?.classList?.contains("absolute") ||
        this._element?.nextElementSibling?.matches?.("[data-dropdown-menu], .absolute.z-50")
          ? this._element.nextElementSibling
          : this._element
              ?.closest(".relative, li, [data-dropdown]")
              ?.querySelector("[data-dropdown-menu], .absolute.z-50.hidden, .absolute.z-50");
      // Fallback: next sibling with absolute positioning classes
      if (!this._menu && this._element?.nextElementSibling) {
        const sib = this._element.nextElementSibling;
        if (sib.className.includes("absolute") || sib.className.includes("dropdown")) {
          this._menu = sib;
        }
      }
      this._isShown = false;
      if (this._element) instances.set(this._element, this);
    }

    static getInstance(element) {
      return instances.get(el(element)) || null;
    }

    static getOrCreateInstance(element) {
      return Dropdown.getInstance(element) || new Dropdown(element);
    }

    static clearMenus(except) {
      document.querySelectorAll("[data-dropdown-menu].is-open, .absolute.z-50.is-open").forEach((menu) => {
        if (except && (menu === except || menu.contains(except))) return;
        menu.classList.add("hidden");
        menu.classList.remove("is-open", "block", "show");
      });
      // Also any menu that lost hidden via show
      document.querySelectorAll(".absolute.z-50.block").forEach((menu) => {
        if (except && (menu === except || menu.contains(except))) return;
        if (!menu.classList.contains("hidden")) {
          menu.classList.add("hidden");
          menu.classList.remove("block", "is-open", "show");
        }
      });
    }

    toggle() {
      return this._menu && !this._menu.classList.contains("hidden") && this._menu.classList.contains("is-open")
        ? this.hide()
        : this._menu && !this._menu.classList.contains("hidden") && !this._menu.classList.contains("is-open")
          ? (this._menu.classList.contains("block") ? this.hide() : this.show())
          : this.show();
    }

    show() {
      if (!this._menu) return;
      Dropdown.clearMenus(this._menu);
      this._menu.classList.remove("hidden");
      this._menu.classList.add("block", "is-open");
      this._element?.setAttribute("aria-expanded", "true");
      this._isShown = true;
    }

    hide() {
      if (!this._menu) return;
      this._menu.classList.add("hidden");
      this._menu.classList.remove("block", "is-open", "show");
      this._element?.setAttribute("aria-expanded", "false");
      this._isShown = false;
    }
  }

  // ─── Collapse ──────────────────────────────────────────────────────
  class Collapse {
    constructor(element, config = {}) {
      this._element = el(element);
      this._config = { toggle: false, ...config };
      this._busy = false;
      if (this._element) instances.set(this._element, this);
    }

    static getInstance(element) {
      return instances.get(el(element)) || null;
    }

    static getOrCreateInstance(element, config) {
      return Collapse.getInstance(element) || new Collapse(element, { toggle: false, ...config });
    }

    toggle() {
      return this._element.classList.contains("hidden") ? this.show() : this.hide();
    }

    show() {
      if (this._busy || !this._element.classList.contains("hidden")) return;
      const parentSel = this._element.dataset.parent || this._element.dataset.bsParent;
      if (parentSel) {
        document.querySelectorAll(parentSel + " .hidden").forEach(() => {});
        document.querySelectorAll(parentSel + " [id]").forEach((node) => {
          if (
            node !== this._element &&
            !node.classList.contains("hidden") &&
            node.classList.contains("overflow-hidden") === false
          ) {
            // close sibling collapses under same parent that look like panels
          }
        });
        const parent = document.querySelector(parentSel);
        parent?.querySelectorAll(":scope .is-collapse-panel").forEach((node) => {
          if (node !== this._element) Collapse.getOrCreateInstance(node).hide();
        });
      }
      this._element.classList.add("is-collapse-panel");
      emit(this._element, "show.bs.collapse");
      this._busy = true;
      this._element.classList.remove("hidden");
      this._element.style.height = "0px";
      this._element.classList.add("overflow-hidden", "transition-[height]", "duration-200");
      const h = this._element.scrollHeight;
      requestAnimationFrame(() => {
        this._element.style.height = h + "px";
      });
      setTimeout(() => {
        this._element.style.height = "";
        this._element.classList.remove("overflow-hidden");
        this._busy = false;
        this._sync(true);
        emit(this._element, "shown.bs.collapse");
      }, 200);
    }

    hide() {
      if (this._busy || this._element.classList.contains("hidden")) return;
      emit(this._element, "hide.bs.collapse");
      this._busy = true;
      this._element.style.height = this._element.scrollHeight + "px";
      void this._element.offsetHeight;
      this._element.classList.add("overflow-hidden", "transition-[height]", "duration-200");
      this._element.style.height = "0px";
      setTimeout(() => {
        this._element.classList.add("hidden");
        this._element.style.height = "";
        this._element.classList.remove("overflow-hidden");
        this._busy = false;
        this._sync(false);
        emit(this._element, "hidden.bs.collapse");
      }, 200);
    }

    _sync(open) {
      const id = this._element.id;
      if (!id) return;
      document
        .querySelectorAll(
          '[data-toggle="collapse"][data-target="#' +
            id +
            '"], [data-toggle="collapse"][href="#' +
            id +
            '"]'
        )
        .forEach((btn) => {
          btn.classList.toggle("collapsed", !open);
          btn.setAttribute("aria-expanded", String(open));
        });
    }
  }

  // ─── Tooltip ───────────────────────────────────────────────────────
  class Tooltip {
    constructor(element) {
      this._element = el(element);
      this._tip = null;
      const title =
        this._element.getAttribute("title") ||
        this._element.getAttribute("data-original-title") ||
        this._element.getAttribute("data-title") ||
        "";
      if (this._element.hasAttribute("title")) {
        this._element.setAttribute("data-original-title", title);
        this._element.removeAttribute("title");
      }
      this._title = title;
      if (!this._title) return;
      this._element.addEventListener("mouseenter", () => this.show());
      this._element.addEventListener("mouseleave", () => this.hide());
      this._element.addEventListener("focus", () => this.show());
      this._element.addEventListener("blur", () => this.hide());
      instances.set(this._element, this);
    }

    static getOrCreateInstance(element) {
      return instances.get(el(element)) || new Tooltip(element);
    }

    show() {
      if (!this._title) return;
      this.hide();
      this._tip = document.createElement("div");
      this._tip.className =
        "pointer-events-none absolute z-[1080] max-w-[220px] rounded-lg bg-black px-2.5 py-1.5 text-xs text-white shadow-md dark:bg-white dark:text-black";
      this._tip.textContent = this._title;
      document.body.appendChild(this._tip);
      const rect = this._element.getBoundingClientRect();
      const tip = this._tip.getBoundingClientRect();
      this._tip.style.top = Math.max(8, rect.top + window.scrollY - tip.height - 8) + "px";
      this._tip.style.left =
        Math.max(8, rect.left + window.scrollX + rect.width / 2 - tip.width / 2) + "px";
    }

    hide() {
      this._tip?.remove();
      this._tip = null;
    }
  }

  // ─── Data API ──────────────────────────────────────────────────────
  document.addEventListener("click", (e) => {
    const dismiss = e.target.closest('[data-dismiss="modal"], [data-close]');
    if (dismiss) {
      const modal = dismiss.closest(".fixed.inset-0, [data-modal], [role='dialog']");
      if (modal) {
        // Don't block form submit buttons — AJAX forms still need to submit.
        const isSubmit =
          dismiss.matches('button[type="submit"], input[type="submit"]') ||
          (dismiss.tagName === "BUTTON" && dismiss.type === "submit");
        if (!isSubmit) e.preventDefault();
        Modal.getOrCreateInstance(modal).hide();
        return;
      }
    }

    const modalTrigger = e.target.closest('[data-toggle="modal"]');
    if (modalTrigger) {
      const sel = targetSelector(modalTrigger);
      if (sel && sel !== "#") {
        const isSubmit =
          modalTrigger.matches('button[type="submit"], input[type="submit"]') ||
          (modalTrigger.tagName === "BUTTON" && modalTrigger.type === "submit");
        if (!isSubmit) e.preventDefault();
        const target = document.querySelector(sel);
        if (target) {
          setTimeout(() => Modal.getOrCreateInstance(target).show(modalTrigger), 0);
        }
      }
      return;
    }

    const ddTrigger = e.target.closest('[data-toggle="dropdown"]');
    if (ddTrigger) {
      e.preventDefault();
      e.stopPropagation();
      Dropdown.getOrCreateInstance(ddTrigger).toggle();
      return;
    }

    const colTrigger = e.target.closest('[data-toggle="collapse"]');
    if (colTrigger) {
      e.preventDefault();
      const sel = targetSelector(colTrigger);
      if (sel) {
        const locked =
          colTrigger.hasAttribute("data-lock") &&
          colTrigger.getAttribute("data-lock") !== "false";
        if (locked) {
          const group = colTrigger.parentElement;
          group
            ?.querySelectorAll('[data-toggle="collapse"][data-lock]')
            .forEach((btn) => {
              const on = btn === colTrigger;
              btn.classList.toggle("active", on);
              btn.classList.toggle("bg-black/5", on);
              btn.classList.toggle("font-semibold", on);
              btn.classList.toggle("dark:bg-white/6", on);
            });
        }
        document.querySelectorAll(sel).forEach((node) => {
          const instance = Collapse.getOrCreateInstance(node);
          if (locked) {
            // Keep the open panel open when its trigger is clicked again
            if (!node.classList.contains("hidden")) return;
            instance.show();
            return;
          }
          instance.toggle();
        });
      }
      return;
    }

    if (!e.target.closest(".absolute.z-50, [data-dropdown-menu]")) {
      Dropdown.clearMenus();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const open = document.querySelector(".fixed.inset-0.is-open, .fixed.inset-0.block:not(.hidden)");
    if (open) {
      const modal = Modal.getOrCreateInstance(open);
      if (modal._config.keyboard === false) return;
      if (open.dataset.keyboard === "false") return;
      modal.hide();
      return;
    }
    Dropdown.clearMenus();
  });

  function initTooltips(root) {
    (root || document).querySelectorAll('[data-toggle="tooltip"]').forEach((node) => {
      Tooltip.getOrCreateInstance(node);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initTooltips());
  } else {
    initTooltips();
  }

  document.addEventListener("click", (e) => {
    const fixed = e.target.closest('[data-toggle="dropdownfixed"]');
    if (!fixed) return;
    e.preventDefault();
    const menu = fixed.parentElement?.querySelector(".absolute.z-50, [data-dropdown-menu]");
    if (!menu) return;
    Dropdown.clearMenus(menu);
    menu.classList.toggle("hidden");
    menu.classList.toggle("is-open");
    menu.classList.toggle("block");
  });

  const api = { Modal, Dropdown, Collapse, Tooltip };
  global.AppUI = api;
  // Compat for custom.js / server.js still calling bootstrap.Modal
  global.bootstrap = api;
})(typeof window !== "undefined" ? window : globalThis);
